import loadable from '@loadable/component';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrency } from '@travel/i18n';
import NavigationTab from '@travel/traveler-core/components/NavigationTab';
import { useTranslation, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { LoadingMask } from '@travel/ui';

import Breadcrumb from 'components/Breadcrumb';
import DataLayer from 'components/DataLayer';
import ScrollingNavigation, { Section } from 'components/ScrollingNavigation';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { TAB_ID } from 'constants/providerInfoTabIds';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import useDialogHandler from 'hooks/useDialogHandler';
import { getIsFetching as getIsBSFetching } from 'store/bookingStep/selectors';
import {
  getHasHighlights,
  getIsFetching as getIsDetailFetching,
  getItem as getProviderItem,
  getProviderFeatures,
} from 'store/providerInformation/selectors';
import { getPlaceName, getSearchFacilities } from 'store/providerList/selectors';
import {
  getIsFetching as getIsPlanListFetching,
  getSelectedFeatures as getPlanSelectedFeatures,
} from 'store/providerPlanList/selectors';
import {
  getAcquiredCoupon,
  getFilterPriceRange,
  getIsFetching as getIsRoomListFetching,
  getItems as getPlans,
  getSalesPromotions,
  getSelectedFeatures as getRoomSelectedFeatures,
  getShouldRenderSimilarProviders,
} from 'store/providerRatePlan/selectors';
import { getReviewListScoreInformation } from 'store/providerReview/selectors';
import usePageLoaderOnInitial from '../../hooks/usePageLoaderOnInitial';
import BackToTopButton from './components/BackToTopButton';
import FindARoomButton from './components/FindARoomButton';
import Overview from './components/Overview';
import ProviderInformationHeader from './components/ProviderInformationHeader';

import { setSigninPopupRAT } from 'store/banner/actions';

import { ProviderInfoQuery } from 'ProviderInformation-Types';
import { isClient } from 'store/utils';
import { generateProviderInfoJsonLd } from './helpers';
import { URLParams } from './resolver';

import { getIsSigninPopupVisible } from 'store/banner/selectors';

import styles from './providerInformation.module.scss';

const RoomAndPlanList = loadable(() =>
  import(
    /* webpackMode: "lazy" */
    /* webpackFetchPriority: "low" */
    /* webpackChunkName: "component-RoomAndPlanList" */
    './components/RoomAndPlanList'
  ),
);

const SimilarProviders = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-SimilarProviders" */
      './components/SimilarProviders'
    ),
  { ssr: false },
);

const BasicInformation = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-BasicInformation" */
      './components/BasicInformation'
    ),
  { ssr: false },
);

const ProviderReviews = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-ProviderReviews" */
      './components/ProviderReviews'
    ),
  { ssr: false },
);

const Highlights = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-Highlights" */
      './components/Highlights'
    ),
  { ssr: false },
);

type Props = {
  query: ProviderInfoQuery;
  params: URLParams;
  /* This is mostly for testing so that this will be false by default on testing,
    except for in the router where it is true */
  shouldLazyLoad?: boolean;
};

function ProviderInformation(props: Props) {
  const { query, params, shouldLazyLoad = false } = props;

  const currencyCode = useSelector(getCurrency);
  const providerMinPrice = useSelector(getFilterPriceRange).min;
  const reviewScoreInfo = useSelector(getReviewListScoreInformation);

  const hasHighlights = useSelector(getHasHighlights);
  const providerItem = useSelector(getProviderItem);
  const itemPlans = useSelector(getPlans);
  const acquiredCoupon = useSelector(getAcquiredCoupon);
  const placeName = useSelector(getPlaceName);
  const [activeTabId, setActiveTabId] = useState<string>(query.activeTabId || TAB_ID.OVERVIEW);
  const [visitedTab, setVisitedTab] = useState<Record<string, string>>({
    ...(query.activeTabId && { [query.activeTabId]: query.activeTabId }),
  });
  const [shouldRenderAll, setRenderAll] = useState(!shouldLazyLoad || false);

  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const searchFacilities: string = useSelector(getSearchFacilities(query?.features));
  const salesPromotions = useSelector(getSalesPromotions);
  const roomIDs = itemPlans.length ? itemPlans.map(item => item.id).join(',') : '';

  const pageName =
    query?.startDate || query?.endDate
      ? 'hotel:rooms_and_plans_dated_list'
      : 'hotel:rooms_and_plans_undated_list';
  const onPage = query?.startDate || query?.endDate ? 'providerInfoDated' : 'providerInfoUndated';

  // page loader
  const isDetailFetching = useSelector(getIsDetailFetching);
  const isRoomListFetching = useSelector(getIsRoomListFetching);
  const isPlanListFetching = useSelector(getIsPlanListFetching);
  const isBookingStepFetching = useSelector(getIsBSFetching);
  const providerFeatures = useSelector(getProviderFeatures);

  const roomSelectedFeatures = useSelector(getRoomSelectedFeatures);
  const planSelectedFeatures = useSelector(getPlanSelectedFeatures);
  const listType = query.listType ? query.listType : PROVIDER_LIST_TYPE.ROOM_LIST;
  const isRoomListType = listType === PROVIDER_LIST_TYPE.ROOM_LIST;
  const selectedFeatures = isRoomListType ? roomSelectedFeatures : planSelectedFeatures;

  const hasSimilarProviders = useSelector(getShouldRenderSimilarProviders);

  const {
    isOpen: isHeaderFloating,
    onOpen: onHeaderFloating,
    onClose: onHeaderStatic,
  } = useDialogHandler(false);

  const overviewRef = useRef<HTMLDivElement | null>(null);
  const roomsAndPlansRef = useRef<HTMLDivElement | null>(null);
  const providerReviewsRef = useRef<HTMLDivElement | null>(null);
  const infoFacilitiesRef = useRef<HTMLDivElement | null>(null);
  const highlightsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!shouldRenderAll) {
      setTimeout(() => {
        setRenderAll(true);
      }, 500);
    }
  }, [shouldRenderAll]);

  usePageLoaderOnInitial(
    'providerInfo',
    isDetailFetching || isRoomListFetching || isPlanListFetching,
  );

  const onClickSeeMoreReview = useCallback(() => {
    if (isSP) {
      setVisitedTab(curr => ({
        ...curr,
        [TAB_ID.REVIEWS]: TAB_ID.REVIEWS,
      }));
      setActiveTabId(TAB_ID.REVIEWS);
    } else {
      providerReviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isSP]);

  const onClickFindARoomButton = useCallback(() => {
    if (isSP) {
      setVisitedTab(curr => ({
        ...curr,
        [TAB_ID.ROOMS_AND_PLANS]: TAB_ID.ROOMS_AND_PLANS,
      }));
      setActiveTabId(TAB_ID.ROOMS_AND_PLANS);
    } else {
      if (isClient) {
        window.scrollTo({
          top: (roomsAndPlansRef.current?.offsetTop ?? 0) - 60,
          behavior: 'smooth',
        });
      }
    }
  }, [isSP]);

  const onChangeHeaderFloatingState = useCallback(
    isFloating => {
      if (isFloating) {
        onHeaderFloating();
      } else {
        onHeaderStatic();
      }
    },
    [onHeaderFloating, onHeaderStatic],
  );

  // Components
  const basicInfo = useMemo(
    () => <BasicInformation pageName={pageName} className={styles.contentBox} params={params} />,
    [pageName, params],
  );

  const overview = useMemo(
    () => (
      <Overview
        className={styles.contentBox}
        pageIdentifier={params.pageIdentifier}
        onPage={onPage}
        pageName={pageName}
        onClickSeeMoreReview={onClickSeeMoreReview}
      />
    ),
    [onClickSeeMoreReview, onPage, pageName, params.pageIdentifier],
  );

  const roomsAndPlans = useMemo(
    () => (
      <RoomAndPlanList
        query={query}
        params={params}
        providerInfoCanonicalUrl={providerItem.pathId}
        shouldLazyLoad={shouldLazyLoad}
      />
    ),
    [params, providerItem.pathId, query, shouldLazyLoad],
  );

  const similarProviders = useMemo(
    () => (
      <SimilarProviders
        query={query}
        className={
          isSP && activeTabId !== TAB_ID.ROOMS_AND_PLANS ? styles.hidden : styles.fullWidthContent
        }
        pageIdentifier={params.pageIdentifier}
      />
    ),
    [activeTabId, isSP, params.pageIdentifier, query],
  );

  const reviews = useMemo(
    () => (
      <ProviderReviews
        onPage={onPage}
        pageIdentifier={params.pageIdentifier}
        className={styles.contentBox}
      />
    ),
    [onPage, params.pageIdentifier],
  );

  const highlights = useMemo(
    () =>
      hasHighlights ? (
        <Highlights
          isDisplayedOnTab={isSP ? activeTabId === TAB_ID.HIGHLIGHTS : null}
          className={styles.contentBox}
        />
      ) : null,
    [isSP, hasHighlights, activeTabId],
  );

  // Labels
  const overviewLabel = useTranslation({ id: 'Provider_Info.Navigation.Overview' });
  const roomsAndPlansLabel = useTranslation({ id: 'Provider_Info.Navigation.Rooms_and_Plans' });
  const reviewsLabel = useTranslation({ id: 'Traveler_Common.Reviews' });
  const basicInfoLabel = useTranslation({ id: 'Provider_Info.Menu.Basic_Info' });
  const highlightsLabel = useTranslation({ id: 'Provider_Info.Menu.Customized' });
  const facilitiesLabel = useTranslation({ id: 'Provider_Info.Provider_Details.Facilities' });

  const tabs = useMemo(
    () =>
      isSP
        ? [
            {
              id: TAB_ID.OVERVIEW,
              title: overviewLabel,
              content: overview,
            },
            {
              id: TAB_ID.ROOMS_AND_PLANS,
              title: roomsAndPlansLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.ROOMS_AND_PLANS]) && roomsAndPlans,
            },
            {
              id: TAB_ID.REVIEWS,
              title: reviewsLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.REVIEWS]) && reviews,
            },
            {
              id: TAB_ID.INFO,
              title: basicInfoLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.INFO]) && basicInfo,
            },
            highlights
              ? {
                  id: TAB_ID.HIGHLIGHTS,
                  title: highlightsLabel,
                  content: (!shouldLazyLoad || visitedTab[TAB_ID.HIGHLIGHTS]) && highlights,
                }
              : null,
          ].filter(tab => tab)
        : EMPTY_ARRAY,
    [
      isSP,
      shouldLazyLoad,
      visitedTab,
      overviewLabel,
      overview,
      roomsAndPlansLabel,
      roomsAndPlans,
      reviewsLabel,
      reviews,
      basicInfoLabel,
      basicInfo,
      highlights,
      highlightsLabel,
    ],
  );

  const scrollingSections = useMemo(
    () =>
      !isSP
        ? ([
            {
              id: TAB_ID.OVERVIEW,
              title: overviewLabel,
              target: overviewRef,
            },
            {
              id: TAB_ID.ROOMS_AND_PLANS,
              title: roomsAndPlansLabel,
              target: roomsAndPlansRef,
            },
            {
              id: TAB_ID.REVIEWS,
              title: reviewsLabel,
              target: providerReviewsRef,
            },
            hasHighlights
              ? {
                  id: TAB_ID.HIGHLIGHTS,
                  title: highlightsLabel,
                  target: highlightsRef,
                }
              : null,
            {
              id: TAB_ID.INFO,
              title: facilitiesLabel,
              target: infoFacilitiesRef,
            },
          ] as Section[])
        : EMPTY_ARRAY,
    [
      facilitiesLabel,
      hasHighlights,
      highlightsLabel,
      isSP,
      overviewLabel,
      reviewsLabel,
      roomsAndPlansLabel,
    ],
  );

  // tab scroll logics
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const prevActiveTabIdRef = useRef(activeTabId);

  const isSigninPopupVisible = useSelector(getIsSigninPopupVisible);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setSigninPopupRAT(RAT_PAGE_TYPE_OBJ[onPage]));
  }, [dispatch, onPage]);
  useEffect(() => {
    if (
      isSP &&
      isHeaderFloating &&
      anchorRef.current &&
      prevActiveTabIdRef.current !== activeTabId
    ) {
      const offsetTop = anchorRef.current?.offsetTop;
      window.scrollTo(0, offsetTop - 50);
    }
  }, [activeTabId, isHeaderFloating, isSP]);

  const onChangeTabs = useCallback(
    (id: string) => {
      prevActiveTabIdRef.current = activeTabId;
      // to re-calculated the text ellipsis when the specific tab is displayed
      setVisitedTab(curr => ({
        ...curr,
        [id]: id,
      }));
      setActiveTabId(id);
    },
    [activeTabId],
  );

  const onClickTab = useCallback(
    (event: React.ChangeEvent) => {
      onChangeTabs(event.currentTarget.id);
    },
    [onChangeTabs],
  );

  useEffect(() => {
    if (isSP && isClient) {
      const url = new URL(window.location.toString());
      if (url.searchParams.get('activeTabId') !== activeTabId) {
        url.searchParams.set('activeTabId', activeTabId);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [activeTabId, isSP]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            generateProviderInfoJsonLd(
              providerItem,
              reviewScoreInfo,
              currencyCode,
              providerMinPrice,
            ),
          )}
        </script>
      </Helmet>

      <div className={styles.container}>
        {isBookingStepFetching && <LoadingMask type="page" />}
        <BackToTopButton />
        <ProviderInformationHeader
          onPage={onPage}
          pageName={pageName}
          className={styles.contentBox}
          pageIdentifier={params.pageIdentifier}
          isHeaderFloating={isHeaderFloating}
          onChangeHeaderFloatingState={onChangeHeaderFloatingState}
        />
      </div>

      {!isSP && (
        <>
          <div className={styles.container}>
            <ScrollingNavigation
              sections={scrollingSections}
              selectedSection={TAB_ID.OVERVIEW}
              otherChildren={<FindARoomButton onClickFindARoomButton={onClickFindARoomButton} />}
            />
            <div ref={overviewRef}>{overview}</div>
            <div ref={roomsAndPlansRef}>{roomsAndPlans}</div>
          </div>
          {hasSimilarProviders && similarProviders}
          {shouldRenderAll && (
            <div className={styles.container}>
              <div ref={providerReviewsRef}>{reviews}</div>
              <div ref={highlightsRef}>{highlights}</div>
              <div ref={infoFacilitiesRef}>{basicInfo}</div>
            </div>
          )}
        </>
      )}

      {/* mobile: tab menu */}
      {isSP && (
        <>
          <div className={styles.container}>
            <div ref={anchorRef} />
            <NavigationTab
              isAnimatedOnStart={false}
              onClick={onClickTab}
              onChange={onChangeTabs}
              className={styles.tab}
              tabMenuClassName={styles.tabMenu}
              selectedTab={activeTabId}
              tabs={tabs}
              hasTouchEvents={false} // removed temporarily for TIDP-33847
            />
          </div>
          {hasSimilarProviders && similarProviders}
        </>
      )}

      <div className={styles.container}>
        <Breadcrumb className={styles.breadcrumb} placeLayers={providerItem.breadcrumb} />
        {!isSigninPopupVisible && (
          <DataLayer
            pageName={pageName}
            pageType="shop_item"
            siteSection="hotel"
            shopId={providerItem.id}
            itemIds={roomIDs}
            salesPromotions={salesPromotions}
            propertyCountry={providerItem.areaInfo?.country}
            hotelRegion={providerItem.areaInfo?.region}
            propertyCity={providerItem.areaInfo?.city}
            propertyDistrict={providerItem.areaInfo?.district}
            numOfAdults={parseInt(query.adults || '')}
            numOfChildren={query?.childrenAges?.split(',')?.length}
            numOfGuest={
              parseInt(query.adults || '') + (query?.childrenAges?.split(',')?.length || 0)
            }
            childrenAges={query?.childrenAges}
            noOfRooms={query?.noOfUnits}
            checkIn={query.startDate}
            checkOut={query.endDate}
            searchCondition={searchFacilities}
            searchQuery={placeName}
            isBookable={itemPlans?.length > 0 ? 'bookable' : 'unbookable'}
            getCouponItem={acquiredCoupon?.ratePlanId && 1}
            updateData={acquiredCoupon?.ratePlanId}
            couponSalesPromotion={acquiredCoupon?.salesPromotionId}
            providerCategory={providerFeatures.categoryFeatures?.join(',')}
            providerFacilities={providerFeatures.facilitiesFeatures?.join(',')}
            searchFilters={selectedFeatures.map(feature => feature.managementName).join(',')}
          />
        )}
        {isSP && (
          <FindARoomButton
            activeTabId={activeTabId}
            onClickFindARoomButton={onClickFindARoomButton}
          />
        )}
      </div>
    </>
  );
}

ProviderInformation.defaultProps = {
  params: {
    pageIdentifier: '',
    locationPath: '',
  },
};

export default ProviderInformation;
